import { take, takeEvery, call, put } from 'redux-saga/effects'
import { SagaIterator, eventChannel, buffers } from 'redux-saga'
import * as da from 'modules/database/actions'
import * as dt from 'modules/database/types'
import * as aa from 'modules/auth/actions'
import { initializeApp } from 'firebase/app'
import { browserLocalPersistence, getAuth, initializeAuth, User } from 'firebase/auth'
import { initializeFirestore as initFirestore, onSnapshot, persistentLocalCache, setDoc, FirestoreSettings, FirestoreError, QuerySnapshot, CACHE_SIZE_UNLIMITED, query, where, getDocs, serverTimestamp, setLogLevel, enablePersistentCacheIndexAutoCreation, getPersistentCacheIndexManager, Unsubscribe, updateDoc, writeBatch, WriteBatch, Timestamp, persistentMultipleTabManager, terminate, clearIndexedDbPersistence, getDocFromCache, QueryDocumentSnapshot, DocumentReference, DocumentData, DocumentSnapshot, UpdateData } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { batch } from 'react-redux'
import { store } from 'modules/root'
import { persistReadyAction } from 'modules/root/actions'
import { db, setDatabase, setStorage } from './index'
import { migrateProduct, migrateProject } from './migration'
import { NOT_LOGGED_IN_ERROR_MESSAGE } from 'modules/root/errors'
import { guarded } from 'modules/root/saga-guard'
import { productReference, productsCollection, projectReference, projectsCollection } from './firebase'
import { cloneDeepWithoutUndefined } from './functions'
import { makeFirestorePatch } from './patch'
import { installFirestoreRecovery } from './recovery'

const WHEN_UPDATED_TIMESTAMP = 'whenUpdatedTimestamp'
const WHEN_UPDATED = 'whenUpdated'

/** How many projects to download products for concurrently while syncing */
const CONCURRENT_PROJECT_DOWNLOADS = 4

/**
 * The most writes we put in one batch. The SDK no longer imposes its old 500-write limit, but the
 * backend caps a commit request's size, so a whole project's worth of products still gets split.
 */
const MAX_BATCH_WRITES = 400

/** How long to wait before retrying projects that failed to download; doubles up to the maximum */
const INITIAL_RETRY_DELAY = 10 * 1000
const MAX_RETRY_DELAY = 5 * 60 * 1000

/**
 * Set this key in localStorage to turn on Firestore's own debug logging, which reports watch stream
 * state, listen targets and connection backoff — the detail we need to explain a read or a sync that
 * stalls while the connection looks healthy. It's very verbose, so it's off unless asked for:
 *
 *     localStorage.setItem('maqasa.firestoreDebug', 'true')
 *
 * then reload. Remove the key to turn it off again.
 */
const FIRESTORE_DEBUG_KEY = 'maqasa.firestoreDebug'

function firestoreDebugEnabled(): boolean {
	try {
		return window.localStorage.getItem(FIRESTORE_DEBUG_KEY) === 'true'
	} catch (error) {
		/* localStorage can throw when storage is disabled */
		return false
	}
}

let unsubscribes: (() => void)[] = []

/**
 * The projects this device has written to, whose acknowledgement the sync hasn't yet seen.
 *
 * Every write bumps the project's whenUpdatedTimestamp with a server timestamp, so once the server
 * acknowledges it the project arrives back through the projects listener looking exactly like a change
 * made on another device. Downloading it again is the safe thing to do — a change from elsewhere can
 * land while our own write is pending, and the merged document is filtered out while it does — but
 * reporting it to the user as an incoming sync is not: the banner would then appear whenever the user
 * edits anything. Remembering what we wrote lets the sync tell its own echo apart from a real remote
 * change, and leave the echo out of what it reports.
 *
 * An entry is held until a snapshot delivers the project with no pending writes, not until the commit
 * promise resolves: those two can happen in either order. A document with no pending writes has had
 * every one of our writes acknowledged, so there's no later echo of this project still to come.
 */
const locallyWrittenProjects = new Set<dt.ProjectID>()

/** Records that we are about to write to a project, so the sync can recognise the write coming back. */
function recordLocalWrite(projectId: dt.ProjectID) {
	locallyWrittenProjects.add(projectId)
}

function* handleUpdateProject(action: da.UpdateProjectAction): SagaIterator {
	if (action.payload) {
		const writes = writeBatch(db)
		uploadProject(writes, action.payload, true)
		recordLocalWrite(action.payload.projectId)
		yield call(() => writes.commit())
	}
}

function* handleUpdateWholeProject(action: ReturnType<typeof da.updateWholeProject>): SagaIterator {
	if (!action.payload) {
		return
	}

	/* Upload the products first. A project's whenUpdatedTimestamp is what tells another device that the
	   project has changed, and the sync takes it as a high-water mark for that project's products, so a
	   product written after it is one the other device will filter out of its download and never ask for
	   again. Every other write here bumps the project last for the same reason.
	 */
	const batches: WriteBatch[] = []
	let writes = writeBatch(db)
	let writeCount = 0
	for (const product of Object.values(action.payload.products)) {
		if (product) {
			if (writeCount >= MAX_BATCH_WRITES) {
				batches.push(writes)
				writes = writeBatch(db)
				writeCount = 0
			}
			yield call(uploadProduct, writes, action.payload.projectId, product, true)
			writeCount++
		}
	}

	/* uploadProject drops the products, which we uploaded individually above */
	uploadProject(writes, action.payload, true)
	batches.push(writes)

	recordLocalWrite(action.payload.projectId)

	/* Commit every batch before awaiting any. A write promise only resolves once the server acknowledges
	   it, but the write itself joins the durable local queue at the commit call — so by the time
	   wholeProjectQueued is dispatched, the whole project can be read back from the cache, and the app
	   dying offline can no longer strand later writes in a suspended saga.
	 */
	const committed = Promise.all(batches.map(writes => writes.commit()))
	yield put(da.wholeProjectQueued(action.payload.projectId))
	yield call(() => committed)
}

function* handleUpdateProduct(action: da.UpdateProductAction): SagaIterator {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	if (action.payload) {
		/* One batch, so the app dying can't keep the product write while losing the project bump that
		   announces it to other devices' sync — the batch is queued whole at the commit call.
		 */
		const writes = writeBatch(db)
		yield call(uploadProduct, writes, action.payload.projectId, action.payload.product, true, action.payload.baseProduct)
		touchProject(writes, user, action.payload.projectId)
		recordLocalWrite(action.payload.projectId)
		yield call(() => writes.commit())
	}
}

function* handleDeleteProduct(action: da.DeleteProductAction): SagaIterator {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	const writes = writeBatch(db)
	writes.update(productReference(user, action.payload.projectId, action.payload.productId),
		'deleted', true,
		'whenDeleted', serverTimestamp(),
	)
	touchProject(writes, user, action.payload.projectId)
	recordLocalWrite(action.payload.projectId)
	yield call(() => writes.commit())
}

function* handleUndeleteProduct(action: da.DeleteProductAction): SagaIterator {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	const writes = writeBatch(db)
	writes.update(productReference(user, action.payload.projectId, action.payload.productId), 'deleted', false)
	touchProject(writes, user, action.payload.projectId)
	recordLocalWrite(action.payload.projectId)
	yield call(() => writes.commit())
}

/** Bump the project's timestamps, which announce a change within the project to other devices' sync. */
function touchProject(writes: WriteBatch, user: User, projectId: dt.ProjectID) {
	writes.update(projectReference(user, projectId),
		WHEN_UPDATED_TIMESTAMP, serverTimestamp(),
		WHEN_UPDATED, new Date().toISOString(),
	)
}

function* handleLoginDone(): SagaIterator {
	console.log('DATABASE: handleLoginDone')
	
	unsubscribes.forEach(u => u())
	unsubscribes = []
	
	yield call(downloadProjects)
}

function* handleLogout(): SagaIterator {
	unsubscribes.forEach(u => u())
	unsubscribes = []

	/* Stop firestore and clear our local data, then restart firestore for the next signin */
	yield call(() => terminate(db))
	yield call(() => clearIndexedDbPersistence(db))

	/* How far the sync got describes the cache we've just thrown away, so it has to go with it */
	clearSyncState()

	/* Any write still waiting for its echo went with the cache too */
	locallyWrittenProjects.clear()

	yield call(initialiseFirestore)
}

/**
 * Emits the browser's online state whenever it changes. The sliding buffer retains the most
 * recent transition so one that occurs before we start consuming the channel isn't lost.
 */
function onlineStateChannel() {
	return eventChannel<boolean>((emit) => {
		const onOnline = () => emit(true)
		const onOffline = () => emit(false)
		window.addEventListener('online', onOnline)
		window.addEventListener('offline', onOffline)
		return () => {
			window.removeEventListener('online', onOnline)
			window.removeEventListener('offline', onOffline)
		}
	}, buffers.sliding(1))
}

function* handleOnlineStateChange(online: boolean): SagaIterator {
	yield put(da.onlineStateChange(online))
}

function uploadProject(writes: WriteBatch, project: DeepReadonly<dt.Project>, updateWhenUpdated: boolean) {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	let _project = { ...project }

	/* Products live in their own subcollection, but callers can hand us a ProjectWithProducts — TypeScript
	   allows it, being a subtype, and there's no excess property check on a variable. setDoc would then embed
	   a copy of every product in the project document, which nothing reads and everything downloads. Drop it
	   here so it can't depend on each call site remembering. setDoc replaces the document, so this also
	   clears the field from projects that were written with it before.
	 */
	delete (_project as Partial<dt.ProjectWithProducts>).products

	_project.whenUpdatedTimestamp = serverTimestamp() as Timestamp
	if (updateWhenUpdated) {
		_project.whenUpdated = new Date().toISOString()
	}
	_project = cloneDeepWithoutUndefined(_project)

	writes.set(projectReference(user, project.projectId), _project)
}

function getProductDocFromCache(ref: DocumentReference<dt.Product, DocumentData>): Promise<DocumentSnapshot<dt.Product, DocumentData>> {
	return getDocFromCache(ref)
}

function* uploadProduct(writes: WriteBatch, projectId: dt.ProjectID, product: DeepReadonly<dt.Product>, updateWhenUpdated: boolean, baseProduct?: DeepReadonly<dt.Product>) {
	const user = getAuth().currentUser
	if (!user) {
		throw new Error(NOT_LOGGED_IN_ERROR_MESSAGE)
	}

	const productDoc = productReference(user, projectId, product.productId)
	let originalProductData: dt.Product | undefined
	let exists: boolean

	try {
		const productSnapshot = (yield call(() => getProductDocFromCache(productDoc))) as Awaited<ReturnType<typeof getProductDocFromCache>>
		/* The cache can also hold the knowledge that the document was deleted, so a snapshot isn't proof of a document */
		exists = productSnapshot.exists()
		originalProductData = baseProduct as dt.Product ?? productSnapshot.data()
	} catch (error) {
		if ((error as FirestoreError).code === 'unavailable') {
			/* The cache doesn't know the document */
			exists = false
		} else {
			throw error
		}
	}

	if (!exists) {
		/* The document is being created, so whatever base we were given, the patch must carry every field —
		   and no deleteFields, which setDoc doesn't accept; an empty base guarantees both.
		 */
		originalProductData = {} as dt.Product
	}

	const update = makeFirestorePatch<dt.Product>(originalProductData, product)
	filterUnwanted(update)

	update.whenUpdatedTimestamp = serverTimestamp()
	if (updateWhenUpdated) {
		update.whenUpdated = new Date().toISOString()
	}

	if (exists) {
		console.debug(`DATABASE: updating project ${projectId} product ${product.productId}`, update)
		writes.update(productDoc, update)
	} else {
		console.debug(`DATABASE: saving new project ${projectId} product ${product.productId}`, update)
		writes.set(productDoc, update)
	}
}

/**
 * The update patch might contain fields _inside_ the whenUpdatedTimestamp, so eliminate those.
 */
function filterUnwanted(update: UpdateData<any>) {
	for (const key of Object.keys(update)) {
		if (key.startsWith(WHEN_UPDATED_TIMESTAMP)) {
			delete update[key]
		}
	}
}

/**
 * How far the sync has got, recorded outside of Firestore's local cache.
 *
 * We used to take this from the cached project documents, using the latest whenUpdatedTimestamp we had.
 * Firestore maintains that cache itself, though: any listener on the projects collection — this one, or the
 * one behind the projects list — writes an updated project document into it as soon as the watch stream
 * delivers it, whether or not we've downloaded that project's products. So the mark moved past changes we
 * had never fetched, and because the listener filters on `>`, those projects were excluded from every later
 * session as well and the change was never seen again. Keeping our own record, written only once the
 * products are in hand, is what lets an interrupted or failed sync recover.
 */
interface SyncState {
	/** Every project updated at or before this has been downloaded, along with its products. */
	projects?: StoredTimestamp
	/** Per project, every one of its products updated at or before this has been downloaded. */
	products?: { [projectId: string]: StoredTimestamp | undefined }
}

/** A Timestamp in a form that survives a round trip through JSON. */
interface StoredTimestamp {
	seconds: number
	nanoseconds: number
}

const SYNC_STATE_KEY_PREFIX = 'maqasa.syncState.'

function syncStateKey(user: User): string {
	return `${SYNC_STATE_KEY_PREFIX}${db.app.options.projectId}.${user.uid}`
}

function loadSyncState(user: User): SyncState {
	try {
		const stored = window.localStorage.getItem(syncStateKey(user))
		if (stored) {
			return JSON.parse(stored) as SyncState
		}
	} catch (error) {
		console.warn('DATABASE: failed to read how far the sync got; syncing from the beginning', error)
	}
	return {}
}

function saveSyncState(user: User, syncState: SyncState) {
	try {
		window.localStorage.setItem(syncStateKey(user), JSON.stringify(syncState))
	} catch (error) {
		console.warn('DATABASE: failed to record how far the sync got; it will repeat itself', error)
	}
}

/** Forget how far the sync got, for when the cache it describes is thrown away. */
function clearSyncState() {
	try {
		const keys: string[] = []
		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i)
			if (key?.startsWith(SYNC_STATE_KEY_PREFIX)) {
				keys.push(key)
			}
		}
		for (const key of keys) {
			window.localStorage.removeItem(key)
		}
	} catch (error) {
		console.warn('DATABASE: failed to clear how far the sync got', error)
	}
}

function storeTimestamp(timestamp: Timestamp): StoredTimestamp {
	return { seconds: timestamp.seconds, nanoseconds: timestamp.nanoseconds }
}

function restoreTimestamp(stored: StoredTimestamp | undefined): Timestamp | undefined {
	return stored ? new Timestamp(stored.seconds, stored.nanoseconds) : undefined
}

/** Whether `timestamp` is later than `then`, compared at the precision Firestore stores. */
function isAfter(timestamp: Timestamp, then: Timestamp | undefined): boolean {
	if (!then) {
		return true
	}
	return timestamp.seconds !== then.seconds ? timestamp.seconds > then.seconds : timestamp.nanoseconds > then.nanoseconds
}

/** Identifies the version of a project we downloaded, so we can tell when there's a newer one. */
function projectVersion(projectDoc: QueryDocumentSnapshot<dt.Project>): string {
	const whenUpdatedTimestamp = projectDoc.data().whenUpdatedTimestamp
	return `${projectDoc.id}@${whenUpdatedTimestamp instanceof Timestamp ? `${whenUpdatedTimestamp.seconds}.${whenUpdatedTimestamp.nanoseconds}` : ''}`
}

/**
 * Orders projects by when they were last updated, most recent first, so that a long sync delivers the
 * projects the user is most likely to open before the ones they've long finished with. A project the
 * server has no timestamp for sorts last.
 */
function byRecency(a: QueryDocumentSnapshot<dt.Project>, b: QueryDocumentSnapshot<dt.Project>): number {
	const aWhenUpdated = a.data().whenUpdatedTimestamp
	const bWhenUpdated = b.data().whenUpdatedTimestamp

	if (!(aWhenUpdated instanceof Timestamp)) {
		return bWhenUpdated instanceof Timestamp ? 1 : 0
	} else if (!(bWhenUpdated instanceof Timestamp)) {
		return -1
	}

	return aWhenUpdated.seconds !== bWhenUpdated.seconds
		? bWhenUpdated.seconds - aWhenUpdated.seconds
		: bWhenUpdated.nanoseconds - aWhenUpdated.nanoseconds
}

/**
 * Download updated projects, and their products, from Firestore so we have them in our local Firestore cache.
 */
async function downloadProjects() {
	const user = getAuth().currentUser
	if (!user) {
		return
	}

	const syncState = loadSyncState(user)
	let projectsWatermark = restoreTimestamp(syncState.projects)

	console.log(`DATABASE: projects are synced up to ${projectsWatermark ? projectsWatermark.toDate().toISOString() : 'the beginning'}`)

	/* Nothing can be downloaded until the watch stream has caught this listener's target up and reported it
	   current, which is the whole of the delay between the app starting and the sync appearing to begin.
	 */
	const listeningSince = Date.now()
	let waitedForServer: number | undefined

	/* The version of each project we've downloaded in this session. A listener's result set only grows while
	   it's open, so this saves us walking back over projects we've already dealt with — the job docChanges()
	   used to do, except that a project whose products failed to download stays absent from this, and so is
	   tried again on the next snapshot rather than being reported once and forgotten.
	 */
	const downloaded = new Set<string>()

	/**
	 * Whether we already hold this exact version of a project.
	 *
	 * We record how far each project is downloaded as we go, so a mark equal to what the server now holds
	 * says this project and its products were downloaded in full, and there's nothing here to ask for. That
	 * is what makes an interrupted sync cheap to resume: the overall mark only moves once a whole snapshot
	 * has landed, so a sync that stopped partway starts its next listen from where it began, but the
	 * projects it did get cost nothing to pass over the second time.
	 */
	const alreadyDownloaded = function(projectDoc: QueryDocumentSnapshot<dt.Project>): boolean {
		const whenUpdatedTimestamp = projectDoc.data().whenUpdatedTimestamp
		const mark = restoreTimestamp(syncState.products?.[projectDoc.id])
		return mark !== undefined && whenUpdatedTimestamp instanceof Timestamp &&
			mark.seconds === whenUpdatedTimestamp.seconds && mark.nanoseconds === whenUpdatedTimestamp.nanoseconds
	}

	/* The versions of projects that are this device's own writes returning from the server, rather than a
	   change made somewhere else. We still download them, but the user made these changes and doesn't need
	   to be told they're arriving. Recorded by version, so a project that fails and is retried stays quiet.
	 */
	const echoedVersions = new Set<string>()

	/** Whether this version of a project is one of our own writes returning from the server. */
	const isEcho = function(projectDoc: QueryDocumentSnapshot<dt.Project>): boolean {
		return echoedVersions.has(projectVersion(projectDoc))
	}

	const handleProject = async function(projectDoc: QueryDocumentSnapshot<dt.Project>) {
		const projectId = projectDoc.id
		const project = projectDoc.data()

		/* Fix invalid whenUpdated dates. There are some invalid whenUpdated timestamps, such as from early development (e.g. Thu, 22 Aug 2019 03:22:02 GMT).
		   These repairs are cosmetic, and a date bad enough that we can't repair it must not fail the project:
		   a failed project holds the sync's watermark back, so one unrepairable document would have every
		   session re-examine everything after it, forever.
		 */
		const fixedWhenUpdated = fixDate(project.whenUpdated)
		if (fixedWhenUpdated === undefined) {
			console.warn(`DATABASE: cannot repair invalid whenUpdated date in project ${projectId}: "${project.whenUpdated}"`)
		} else if (fixedWhenUpdated !== project.whenUpdated) {
			console.log(`DATABASE: fixing invalid whenUpdated date in project ${projectId} from "${project.whenUpdated}" to "${fixedWhenUpdated}`)

			project.whenUpdated = fixedWhenUpdated
			await updateDoc(projectDoc.ref, WHEN_UPDATED, fixedWhenUpdated)
		}

		if (project.archived === undefined) {
			console.log(`DATABASE: fixing missing archived flag in project ${projectId}`)
			project.archived = !!project.whenArchived
			await updateDoc(projectDoc.ref, 'archived', project.archived)
		}

		let whenUpdatedTimestamp = project.whenUpdatedTimestamp
		if (whenUpdatedTimestamp === undefined) { /* null means it was created locally and hasn't yet gone to the server */
			const derived = new Date(project.whenUpdated)
			if (isNaN(derived.getTime())) {
				console.warn(`DATABASE: cannot derive ${WHEN_UPDATED_TIMESTAMP} in project ${projectId} from "${project.whenUpdated}"`)
			} else {
				whenUpdatedTimestamp = project.whenUpdatedTimestamp = Timestamp.fromDate(derived)

				console.log(`DATABASE: adding ${WHEN_UPDATED_TIMESTAMP} in project ${projectId} from "${project.whenUpdated}" to "${whenUpdatedTimestamp.toDate().toISOString()}"`)
				await updateDoc(projectDoc.ref, WHEN_UPDATED_TIMESTAMP, whenUpdatedTimestamp)
			}
		}

		console.log(`DATABASE: project ${projectId} last updated at ${whenUpdatedTimestamp instanceof Timestamp ? whenUpdatedTimestamp.toDate().toISOString() : 'just now'}`)

		const migrated = migrateProject(project)
		if (migrated) {
			console.log(`DATABASE: migrated project ${projectId}`)
			project.whenUpdatedTimestamp = serverTimestamp() as Timestamp

			/* Older project documents embed a copy of every product; don't write it back out again */
			delete (project as Partial<dt.ProjectWithProducts>).products

			recordLocalWrite(projectId)
			await setDoc(projectDoc.ref, project)
		}

		/* Ensure that the local cache has got all of the updated product docs.

		   The floor is this project's own, not how far the sync has got overall: another project updated in
		   between would carry the overall mark past products that were written before the project document
		   we're looking at now, and this query would filter out the very products it was told to fetch. A
		   project we have no mark for is one whose products we've never downloaded, so we take the lot.
		 */
		const productsFloor = restoreTimestamp(syncState.products?.[projectId])
		const productsQuery = productsFloor
			? query(productsCollection(user, projectId), where(WHEN_UPDATED_TIMESTAMP, '>=', productsFloor))
			: query(productsCollection(user, projectId))
		const productsStarted = Date.now()
		const productDocs = await getDocs(productsQuery)

		/* getDocs prefers the server but quietly answers from the cache when the server can't be reached.
		   Accepting that answer would record this project as downloaded at the server's timestamp with
		   products we never actually fetched, and the marks would then pass over it for good — the very
		   loss this sync's own record exists to prevent. Failing the project keeps its mark where it was,
		   and the retry brings us back.
		 */
		if (productDocs.metadata.fromCache) {
			throw new Error(`products for project ${projectId} were answered from the cache, not the server`)
		}

		console.log(`DATABASE: found ${productDocs.size} products in project ${projectId}${productsFloor ? ` updated since ${productsFloor.toDate().toISOString()}` : ''} in ${Date.now() - productsStarted}ms`)

		const downloadedProducts: da.DownloadedProductPayload[] = []
		for (const productDoc of productDocs.docs) {
			const product = productDoc.data()
			const migrated = migrateProduct(product)
			if (migrated) {
				console.log(`DATABASE: migrated project ${projectId} product ${product.productId}`)
				product.whenUpdatedTimestamp = serverTimestamp() as Timestamp
				await setDoc(productDoc.ref, product)
				recordLocalWrite(projectId)
				await updateDoc(projectDoc.ref, WHEN_UPDATED_TIMESTAMP, serverTimestamp())
			}

			downloadedProducts.push({
				projectId,
				product,
			})
		}

		/* Dispatch in a single batch so connected components are notified once per project, not once per document */
		console.debug(`DATABASE: notifying downloaded project ${projectId} with ${downloadedProducts.length} products`)
		batch(() => {
			for (const payload of downloadedProducts) {
				store.dispatch(da.downloadedProduct(payload))
			}
			store.dispatch(da.downloadedProject({ projectId, project }))
			if (!isEcho(projectDoc)) {
				store.dispatch(da.syncProgress(1))
			}
		})

		/* Only now the products are in hand do we record how far this project has been downloaded. Whatever
		   the server holds for the project is a safe mark for its products, as they're always written first.
		 */
		if (whenUpdatedTimestamp instanceof Timestamp) {
			const products = syncState.products || (syncState.products = {})
			products[projectId] = storeTimestamp(whenUpdatedTimestamp)
			saveSyncState(user, syncState)
		}

		/* TESTING:
		 * Add a product to a project. Check that it appears.
		 * Remove a product from a project. Check that it disappears.
		 * Type into a text box and check that your typing doesn't get clobbered / that you lose characters.
		 */
	}

	/* Load all projects that have changed */
	let unsubscribe: Unsubscribe | undefined

	/* The versions of projects the sync banner's found-count has already included, so a project that failed
	   and is being retried isn't counted as found again.
	 */
	const reportedVersions = new Set<string>()

	/** Processes one snapshot, returning the number of projects that failed to download. */
	const processSnapshot = async function(docs: QuerySnapshot<dt.Project>): Promise<number> {
		/* Only the server can tell us what another device has changed. A snapshot raised from the cache
		   reports what we already hold locally, so there's nothing here to download — and acting on one would
		   let us record projects as downloaded whose products we had never asked the server for.
		 */
		if (docs.metadata.fromCache) {
			console.log(`DATABASE: ignoring a snapshot of ${docs.size} projects raised from the cache ${Date.now() - listeningSince}ms after listening`)
			return 0
		}

		if (waitedForServer === undefined) {
			waitedForServer = Date.now() - listeningSince
			console.log(`DATABASE: the server caught this listener up ${waitedForServer}ms after listening`)
		}

		/* A document with no pending writes has had every one of our own writes to it acknowledged, so this
		   is the version those writes produced, and no later echo of them is still to come. Recognise it
		   before the filters below, which would otherwise leave the project marked as locally written for
		   good, and the next genuine change from another device would be taken for the echo we never saw.
		 */
		for (const doc of docs.docs) {
			if (!doc.metadata.hasPendingWrites && locallyWrittenProjects.delete(doc.id)) {
				echoedVersions.add(projectVersion(doc))
			}
		}

		/* Work from the whole result set rather than docChanges(), which reports a project once and never
		   again — leaving nothing to come back to when its products fail to download. A document with pending
		   writes carries an unresolved server timestamp, so there's nothing to measure it against; we'll see
		   it again once the write is acknowledged.

		   Firestore orders an unordered query by document id, which tells the user nothing, so take the most
		   recently updated projects first.
		 */
		const projectDocs = docs.docs
			.filter(doc => !doc.metadata.hasPendingWrites && !downloaded.has(projectVersion(doc)) && !alreadyDownloaded(doc))
			.sort(byRecency)

		/* Our own changes are downloaded like any others, but they're not news to the user, so they're left
		   out of everything the sync reports. A snapshot that holds nothing else raises no banner at all.
		 */
		const remoteDocs = projectDocs.filter(doc => !isEcho(doc))
		const echoCount = projectDocs.length - remoteDocs.length

		console.log(`DATABASE: ${projectDocs.length} of ${docs.size} projects updated since ${projectsWatermark?.toDate().toISOString() ?? 'the beginning'} to download${echoCount ? `, ${echoCount} of them our own changes returning` : ''}`)
		if (!projectDocs.length) {
			return 0
		}

		const newlyFound = remoteDocs.filter(doc => !reportedVersions.has(projectVersion(doc)))
		newlyFound.forEach(doc => reportedVersions.add(projectVersion(doc)))

		const started = Date.now()
		if (remoteDocs.length) {
			batch(() => {
				store.dispatch(da.syncing(true))
				if (newlyFound.length) {
					store.dispatch(da.syncFound(newlyFound.length))
				}
			})
		}

		/* Fetching each project's products requires a server round trip, which dominates the time a large
		   initial sync takes, so process a few projects concurrently — a shared queue, so a slow project
		   only occupies its own worker. A project that fails only costs us that project; the rest of the
		   snapshot still lands.
		 */
		let newest: Timestamp | undefined
		let failed = 0
		let nextIndex = 0
		const worker = async function() {
			while (nextIndex < projectDocs.length) {
				const projectDoc = projectDocs[nextIndex]
				nextIndex++
				try {
					await handleProject(projectDoc)

					downloaded.add(projectVersion(projectDoc))

					const whenUpdatedTimestamp = projectDoc.data().whenUpdatedTimestamp
					if (whenUpdatedTimestamp instanceof Timestamp && isAfter(whenUpdatedTimestamp, newest)) {
						newest = whenUpdatedTimestamp
					}
				} catch (error) {
					console.warn(`DATABASE: failed to download project ${projectDoc.id}`, error)
					failed++
				}
			}
		}
		await Promise.all(Array.from({ length: Math.min(CONCURRENT_PROJECT_DOWNLOADS, projectDocs.length) }, worker))

		console.log(`DATABASE: downloaded ${projectDocs.length - failed} projects in ${Date.now() - started}ms${failed ? `, ${failed} failed` : ''}`)

		/* The mark says everything at or before it has been downloaded, so it can only move once the whole
		   snapshot has landed — taking the newest first means there's no earlier point that's safe. Moving it
		   past a project that failed would put the next session's query beyond that project, and nothing
		   would bring us back to it. A sync cut short keeps the mark it started with and lists these projects
		   again next time, but alreadyDownloaded() then passes over the ones it got without a server read.
		 */
		if (failed) {
			return failed
		}
		if (newest && isAfter(newest, projectsWatermark)) {
			projectsWatermark = newest
			syncState.projects = storeTimestamp(newest)
			saveSyncState(user, syncState)
			console.log(`DATABASE: projects are synced up to ${newest.toDate().toISOString()}`)
		}
		return 0
	}

	/* onSnapshot can deliver another snapshot while a previous async handler is still awaiting server
	   reads, which would interleave processing and duplicate work. A listener's snapshots are cumulative,
	   so ones that queue up while we work are superseded by the latest — only that one is kept to process
	   next. The pump must never reject, otherwise snapshots would be silently dropped.

	   A project that fails to download is only retried when something else prompts a snapshot, which
	   might be never, so a snapshot that had failures is retried on a timer. A newer snapshot supersedes
	   any scheduled retry, as it contains the failed projects as well.
	 */
	let pendingSnapshot: QuerySnapshot<dt.Project> | undefined
	let pumping = false
	let stopped = false
	let retryTimer: ReturnType<typeof setTimeout> | undefined
	let retryDelay = INITIAL_RETRY_DELAY

	const pump = async function() {
		pumping = true

		const scheduleRetry = function(docs: QuerySnapshot<dt.Project>) {
			if (stopped) {
				/* The listener was unsubscribed while its snapshot was being processed */
				return
			}
			console.log(`DATABASE: will retry failed projects in ${retryDelay}ms`)
			retryTimer = setTimeout(() => {
				retryTimer = undefined
				if (!pendingSnapshot) {
					pendingSnapshot = docs
				}
				if (!pumping) {
					pump()
				}
			}, retryDelay)
			retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY)
		}

		try {
			while (pendingSnapshot) {
				/* Whatever this snapshot holds supersedes any retry that was waiting */
				if (retryTimer) {
					clearTimeout(retryTimer)
					retryTimer = undefined
				}

				const docs = pendingSnapshot
				pendingSnapshot = undefined

				try {
					const failed = await processSnapshot(docs)
					if (failed) {
						scheduleRetry(docs)
					} else {
						retryDelay = INITIAL_RETRY_DELAY
					}
				} catch (error) {
					console.warn('DATABASE: failed to process snapshot', error)
					scheduleRetry(docs)
				}
			}
		} finally {
			pumping = false
			store.dispatch(da.syncing(false))
		}
	}

	const handleSnapshot = function(docs: QuerySnapshot<dt.Project>) {
		console.log(`DATABASE: snapshot received of ${docs.size} projects${pendingSnapshot ? ', superseding one queued' : ''}`)
		pendingSnapshot = docs
		if (!pumping) {
			pump()
		}
	}

	if (projectsWatermark) {
		console.log(`DATABASE: starting with query for projects changed since ${projectsWatermark.toDate().toISOString()}`)
		unsubscribe = onSnapshot(query(projectsCollection(user), where(WHEN_UPDATED_TIMESTAMP, '>', projectsWatermark)), handleSnapshot)
	} else {
		console.log('DATABASE: starting with query for all projects')
		unsubscribe = onSnapshot(query(projectsCollection(user)), handleSnapshot)
	}
	unsubscribes.push(unsubscribe)
	unsubscribes.push(() => {
		stopped = true
		if (retryTimer) {
			clearTimeout(retryTimer)
			retryTimer = undefined
		}
		pendingSnapshot = undefined
	})
}

/**
 * Some old data had dates in a Date.toString() form so we fix them to the ISO date standard.
 * Returns `undefined` for a date that can't be made sense of, rather than throwing: these repairs
 * happen while downloading, where an error fails the whole project.
 * @param dateString
 */
function fixDate(dateString: string): string | undefined {
	if (typeof dateString === 'string') {
		if (dateString.match(/^[0-9]/)) {
			return dateString
		}
		const parsed = new Date(dateString)
		return isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
	} else if ((dateString as any) instanceof Timestamp) {
		return (dateString as unknown as Timestamp).toDate().toISOString()
	} else {
		return undefined
	}
}

async function initialiseFirestore(): Promise<void> {
	console.log('DATABASE: initialising firestore')

	installFirestoreRecovery()

	if (firestoreDebugEnabled()) {
		console.log('DATABASE: firestore debug logging is enabled')
		setLogLevel('debug')
	}

	const config = {
		apiKey: 'AIzaSyD6eZkicIvrDOK8tKQ3slWqAInYUXQtYOU',
		authDomain: 'maqasa-development.firebaseapp.com',
		databaseURL: 'https://maqasa-development.firebaseio.com',
		projectId: 'maqasa-development',
		storageBucket: 'maqasa-development.appspot.com',
		messagingSenderId: '547833136100',
	}
	const app = initializeApp(config)
	const auth = initializeAuth(app, {
		persistence: browserLocalPersistence,
	})
	console.log('AUTH: initialised', auth.currentUser ? '(signed in)' : '(not signed in)')

	const storage = getStorage()
	setStorage(storage)
	console.log('STORAGE: initialised')

	const settings: FirestoreSettings = {
		localCache: persistentLocalCache({
			cacheSizeBytes: CACHE_SIZE_UNLIMITED,
			tabManager: persistentMultipleTabManager(),
		}),
		/* Undefined values can't be stored, and without this a single one anywhere in a write throws.
		   Dropping them matches what cloneDeepWithoutUndefined does where we clean explicitly.
		 */
		ignoreUndefinedProperties: true,
	}
	const newDatabase = initFirestore(app, settings)

	/* Without local cache indexes every query against the cache is a full collection scan — the debug log
	   reports "Using full collection scan to execute query" for all of ours, including the projects queries
	   that run on every sync. This lets the SDK build indexes for the queries it sees, which costs a
	   background backfill but makes those scans proportional to the result rather than the whole collection.
	 */
	const indexManager = getPersistentCacheIndexManager(newDatabase)
	if (indexManager) {
		enablePersistentCacheIndexAutoCreation(indexManager)
		console.log('DATABASE: persistent cache index auto-creation enabled')
	} else {
		console.warn('DATABASE: no persistent cache index manager; queries will use full collection scans')
	}

	setDatabase(newDatabase)
}

/** The saga function for this module that is run from our root saga. */
export default function* databaseSaga(): SagaIterator {
	/* Start listening for online state transitions immediately so we don't miss any while initialising */
	const onlineChannel = onlineStateChannel()

	yield take(persistReadyAction)
	yield call(initialiseFirestore)

	console.log('DATABASE: initialised')
	yield put(da.databaseReady())

	yield takeEvery(da.updateProject, guarded(handleUpdateProject))
	yield takeEvery(da.updateWholeProject, guarded(handleUpdateWholeProject))
	yield takeEvery(da.updateProduct, guarded(handleUpdateProduct))
	yield takeEvery(da.deleteProduct, guarded(handleDeleteProduct))
	yield takeEvery(da.undeleteProduct, guarded(handleUndeleteProduct))
	yield takeEvery(aa.loggedIn, guarded(handleLoginDone))
	yield takeEvery(aa.loggedOut, guarded(handleLogout))

	yield takeEvery(onlineChannel, handleOnlineStateChange)

	/* Init our online state from the browser, in case we missed a transition while we were waiting to be initialised */
	yield put(da.onlineStateChange(navigator.onLine))
}
